import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Modal, Space, Typography } from "antd";

import type { User } from "@chirpstack/chirpstack-api-grpc-web/api/user_pb";

// First-login "what do you want to do with the welcome-email password?"
// nudge. When SM provisions a new tenant admin it emails them a
// generated password; on their first successful sign-in we ask whether
// they want to rotate it now or keep it. Either answer persists to
// localStorage so the modal doesn't fire on every subsequent login.
//
// Storage strategy (Option L per the Slice 5 brief): localStorage
// keyed by user id. Deliberately client-side — a server-side
// `first_login_acked` flag would require a schema migration and gRPC
// surface for minimal product benefit. Worst case someone clears their
// browser and sees the modal a second time; that's acceptable.
//
// A later phase (Phase 3 Rust enforcement) may supersede this with a
// real server-side flag; the localStorage key would then be treated as
// a fallback when the server flag is absent.

const STORAGE_PREFIX = "ioxFirstLoginAcked:";

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function markFirstLoginAcked(userId: string): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), "1");
  } catch {
    // localStorage can throw in private-mode Safari or when quota is
    // exhausted. Swallowing here means the modal may re-fire — which is
    // a better failure mode than refusing to dismiss.
  }
}

function hasAckedFirstLogin(userId: string): boolean {
  if (!userId) return true; // defensive: no user id ⇒ nothing to ack
  try {
    return localStorage.getItem(storageKey(userId)) !== null;
  } catch {
    return true;
  }
}

interface IProps {
  user: User;
}

function FirstLoginPasswordModal(props: IProps) {
  const navigate = useNavigate();
  const userId = props.user.getId();
  const [open, setOpen] = useState(false);

  // Re-evaluate whenever the signed-in user changes (login, user-switch).
  // We *don't* use a useEffect with [userId] alone because a logout
  // followed by a login by the same user would leave `open` stuck at
  // false — using a fresh effect keyed off userId resets that.
  useEffect(() => {
    if (!userId) {
      setOpen(false);
      return;
    }
    setOpen(!hasAckedFirstLogin(userId));
  }, [userId]);

  const onKeep = () => {
    markFirstLoginAcked(userId);
    setOpen(false);
  };

  const onChange = () => {
    // Ack first so when the user lands on the change-password page and
    // navigates away, the modal doesn't fire again. If they bail on the
    // change-password form itself that's fine — they explicitly picked
    // "change", so pestering them further is noise.
    markFirstLoginAcked(userId);
    setOpen(false);
    navigate(`/users/${userId}/password`);
  };

  return (
    <Modal
      open={open}
      title="Welcome! Set your password?"
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={480}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          You signed in with the temporary password sent in your welcome email.
          Would you like to change it now, or keep using the one you received?
        </Typography.Paragraph>
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onKeep}>Keep password</Button>
          <Button type="primary" onClick={onChange}>
            Change password
          </Button>
        </Space>
      </Space>
    </Modal>
  );
}

export default FirstLoginPasswordModal;
